import { useCallback, useEffect, useState } from "react"
import { MatsnetLeaderboardEntry } from "@mezo/api"
import { useAccount } from "wagmi"

import {
  fetchMatsnetLeaderboard,
  selectFullMatsnetLeaderboard,
  selectIsMatsnetLeaderboardFullyFetched,
  selectMatsnetUserRank,
  selectMatsnetUsersCount,
  setConnectedChainId,
  stackMatsnetBtc,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { LEADERBOARD_FETCH_CHUNK, MEZO_CHAIN } from "#/shared/constants"

import { LeaderboardProps, LeaderboardRank } from "../types"
import { separateThousandsWithComaRegExp } from "../utils"

import { useLeaderboardRankCalculations } from "./leaderboard"
import { useFetchMats } from "./mats"
import { usePosthogMatsConvertedEvent } from "./posthog-events"

// TODO: The file and hooks names are no longer accurate. `matsnet` is not longer a thing,

export const useMatsnetLeaderboard =
  (): LeaderboardProps<MatsnetLeaderboardEntry> => {
    const leaderboard = useDappSelector(selectFullMatsnetLeaderboard)
    const userRank = useDappSelector(selectMatsnetUserRank)

    const isLeaderboardFullyFetched = useDappSelector(
      selectIsMatsnetLeaderboardFullyFetched,
    )

    const dispatch = useDappDispatch()

    useEffect(() => {
      const loadLeaderboard = async () => {
        if (leaderboard.length) return

        await dispatch(
          fetchMatsnetLeaderboard({ start: 0, end: LEADERBOARD_FETCH_CHUNK }),
        )
      }

      loadLeaderboard()
    }, [dispatch, leaderboard])

    const loadMoreHandler = useCallback(async () => {
      await dispatch(
        fetchMatsnetLeaderboard({
          start: leaderboard.length,
          end: leaderboard.length + LEADERBOARD_FETCH_CHUNK,
        }),
      )
    }, [dispatch, leaderboard.length])

    return {
      leaderboard,
      isLeaderboardFullyFetched,
      loadMore: loadMoreHandler,
      currentUserRank: userRank.rank,
    }
  }

export const useMatsnetLeaderboardRank = (): LeaderboardRank => {
  const { rank } = useDappSelector(selectMatsnetUserRank)
  const numberOfRankedUsers = useDappSelector(selectMatsnetUsersCount)

  const rankData = useLeaderboardRankCalculations(rank, numberOfRankedUsers)

  return rankData
}

export const useStackMatsnetBtcModal = (onClose: () => void) => {
  const [displaySuccessModal, setDisplaySuccessModal] = useState(false)
  const [stackedSats, setStackedSats] = useState("")

  /**
   * Formats stacked sats to be displayed for success modal
   * If the value (sats) is less than 1 billion then we just display it
   * normally, with thousands separator.
   * If the value (sats) is equal or greater than 1 billion, then we display a
   * shortened version with two decimal places (rounded down always).
   *
   * Examples:
   * 1,000,000,000 => 1B
   * 1,200,000,000 => 1.2B
   * 1,230,000,000 => 1.23B
   * 1,234,000,000 => 1.23B
   * 1,239,000,000 => 1.23B
   *
   * The formatted value also uses thousands separator (if it's big enough):
   * 1,555,230,000,000 => 1,555.23B
   * 1,555,666,230,000,000 => 1,555,666.23B
   */
  const formatStackedSatsForSuccessModal = (sats?: string) => {
    if (!sats) return sats
    // remove thousand separators
    const bigIntVal = BigInt(sats.replace(/,/g, ""))

    // if value is bigger than 1B
    if (bigIntVal >= 1_000_000_000n) {
      // remove last 7 digits so that we only have two decimal places (at most)
      const tempValue = bigIntVal.toString().slice(0, -7)

      // add dot to add decimal places
      const tempValueWithDecimalPlaces = `${tempValue.slice(
        0,
        -2,
      )}.${tempValue.slice(-2)}`

      // remove trailing zeroes
      const valueWithDecimalPlaces = tempValueWithDecimalPlaces.replace(
        /(\.00|(\.\d)0)$/,
        "$2",
      )

      const valueSplitted = valueWithDecimalPlaces.split(".")
      if (valueSplitted.length === 1) {
        return `${separateThousandsWithComaRegExp(valueSplitted[0])}B`
      }

      return `${separateThousandsWithComaRegExp(valueSplitted[0])}.${
        valueSplitted[1]
      }B`
    }

    return sats
  }

  const onSuccess = (sats?: string) => {
    setStackedSats(sats || "")
    setDisplaySuccessModal(true)
  }

  const closeModal = useCallback(() => {
    setDisplaySuccessModal(false)
    onClose()
  }, [onClose])

  return {
    displaySuccessModal,
    stackedSats,
    formatStackedSatsForSuccessModal,
    onSuccess,
    closeModal,
  }
}

export const useStackMatsnetBtcForm = (
  onSuccessCallback?: (stackedSats?: string, address?: string) => void,
) => {
  const dispatch = useDappDispatch()

  const { emitMatsConvertedEvent } = usePosthogMatsConvertedEvent()
  const { address } = useAccount()
  const { fetchMats } = useFetchMats()

  const [matsToRevoke, setMatsToRevoke] = useState("")
  const [hasInputError, setHasInputError] = useState(false)
  const [hasApiError, setHasApiError] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const [userFriendlyMatsToRevoke, setUserFriendlyMatsToRevoke] = useState("")

  const [userFriendlySatsToGet, setUserFriendlySatsToGet] = useState("")

  const handleInputChange = useCallback((amount: string) => {
    const amountWithoutDots = amount.replace(/\./g, "")

    const formattedAmountWithoutDots =
      separateThousandsWithComaRegExp(amountWithoutDots)

    setMatsToRevoke(amountWithoutDots)
    setUserFriendlyMatsToRevoke(
      amountWithoutDots ? formattedAmountWithoutDots : "",
    )
    setUserFriendlySatsToGet(
      amountWithoutDots
        ? separateThousandsWithComaRegExp(
            (BigInt(amountWithoutDots) * 10n).toString(),
          )
        : "",
    )
  }, [])

  const errorCallback = (_hasError: boolean) => {
    setHasInputError(_hasError)
  }

  const stackMatsnetBtcHandler = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setHasApiError(false)
      setIsProcessing(true)

      if (!address) {
        setHasApiError(true)
        return
      }

      const { payload } = (await dispatch(
        stackMatsnetBtc({
          mats: Number(matsToRevoke),
        }),
      )) as { payload: { success: boolean } }

      setIsProcessing(false)

      if (payload.success && onSuccessCallback) {
        onSuccessCallback(userFriendlySatsToGet, address)
        emitMatsConvertedEvent(address, matsToRevoke)
        await fetchMats()
      }

      if (!payload.success) setHasApiError(true)
    },
    [
      address,
      dispatch,
      matsToRevoke,
      onSuccessCallback,
      userFriendlySatsToGet,
      emitMatsConvertedEvent,
      fetchMats,
    ],
  )

  return {
    matsToRevoke,
    userFriendlyMatsToRevoke,
    userFriendlySatsToGet,
    hasInputError,
    hasApiError,
    isProcessing,
    handleInputChange,
    errorCallback,
    stackMatsnetBtc: stackMatsnetBtcHandler,
  }
}

export const useAddMezoChainToWallet = (isEthereumFlow: boolean) => {
  const [isMezoChain, setIsMezoChain] = useState(false)

  const hexadecimalMezoChainId = `0x${MEZO_CHAIN.id.toString(16)}`

  useEffect(() => {
    const getCurrentChainId = async () => {
      if (!window.ethereum || !isEthereumFlow) return

      const currentChainId = await window.ethereum.request({
        method: "eth_chainId",
      })

      setIsMezoChain(currentChainId === hexadecimalMezoChainId)
    }

    getCurrentChainId()
  }, [isEthereumFlow, hexadecimalMezoChainId])

  const addMezoChain = useCallback(async () => {
    if (!window.ethereum || !isEthereumFlow) return

    const mezoChainParams = {
      chainId: hexadecimalMezoChainId,
      chainName: MEZO_CHAIN.name,
      nativeCurrency: MEZO_CHAIN.nativeCurrency,
      rpcUrls: [MEZO_CHAIN.rpcUrls.default.http[0]],
      blockExplorerUrls: [MEZO_CHAIN.blockExplorers?.default.url],
    }

    try {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [mezoChainParams],
      })

      setIsMezoChain(true)
      setConnectedChainId(MEZO_CHAIN.id)
    } catch (err) {
      setIsMezoChain(false)
    }
  }, [isEthereumFlow, hexadecimalMezoChainId])

  return { isMezoChain, addMezoChain }
}
